import { useEffect, useRef, useState } from "react";
import { googleLogin } from "../services/auth";
import { clearUserStorage, saveUserToken, saveAddresses, getDeviceId } from "../utils/tokenUtils";
import { getCustomerAddressList } from "../services/customerAddress";
import { useLanguage } from "../context/LanguageContext";
import { LEGAL_PATHS } from "../legal/legalContent";

const GOOGLE_CLIENT_ID =
  "379059299089-6495btltoajgsad1n7vkr0obutafnpah.apps.googleusercontent.com";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            cancel_on_tap_outside?: boolean;
          }) => void;
          renderButton: (parent: HTMLElement, options: object) => void;
        };
      };
    };
    // Mobil uygulamanın (ReactNativeApp) WebView'ı tarafından eklenir
    ReactNativeWebView?: { postMessage: (message: string) => void };
  }
}

// Google girişi uygulamanın WebView'ında çalışmaz; uygulama içindeysek giriş
// sayfası cihazın tarayıcısında açılır, gelen kimlik bilgisi bu olayla geri döner.
const NATIVE_CREDENTIAL_EVENT = "native-google-credential";

const buildNativeGoogleAuthUrl = () => {
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: `${window.location.origin}/google-callback`,
    response_type: "id_token",
    scope: "openid email profile",
    nonce: `${Date.now()}${Math.random().toString(36).slice(2)}`,
    state: "app",
    prompt: "select_account",
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
};

interface Props {
  onLoginSuccess: () => void;
}

function GoogleLoginButton({ onLoginSuccess }: Props) {
  const { t } = useLanguage();
  const [error, setError] = useState("");
  const btnRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);
  const isInApp = !!window.ReactNativeWebView;
  // Sayfada birden fazla Google butonu olabilir; sadece tıklanan sonucu işler
  const nativePendingRef = useRef(false);

  const handleCredential = async (credential: string) => {
    setError("");
    try {
      const res = await googleLogin(credential, getDeviceId());
      clearUserStorage();
      saveUserToken({
        token: res.data.token,
        expiration: res.data.expiration,
        secretKey: res.data.secretKey,
        userId: res.data.userId,
        refreshToken: res.data.refreshToken,
        refreshTokenExpiration: res.data.refreshTokenExpiration,
      });
      try {
        const addrRes = await getCustomerAddressList(res.data.userId);
        saveAddresses(addrRes.data);
      } catch {
        // Adres çekme hatası login'i engellemesin
      }
      onLoginSuccess();
    } catch {
      setError(t("login.googleFailed"));
    }
  };

  const renderGoogleButton = () => {
    if (!window.google || !btnRef.current || initializedRef.current) return;
    initializedRef.current = true;

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (response) => handleCredential(response.credential),
      cancel_on_tap_outside: true,
    });

    window.google.accounts.id.renderButton(btnRef.current, {
      theme: "outline",
      size: "large",
      shape: "pill",
      text: "signin_with",
      width: btnRef.current.offsetWidth || 300,
    });
  };

  const startNativeLogin = () => {
    setError("");
    nativePendingRef.current = true;
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: "GOOGLE_LOGIN", url: buildNativeGoogleAuthUrl() })
    );
  };

  useEffect(() => {
    if (!isInApp) return;
    const handleNativeCredential = (e: Event) => {
      if (!nativePendingRef.current) return;
      nativePendingRef.current = false;
      const credential = (e as CustomEvent<string | null>).detail;
      if (credential) handleCredential(credential);
    };
    window.addEventListener(NATIVE_CREDENTIAL_EVENT, handleNativeCredential);
    return () => window.removeEventListener(NATIVE_CREDENTIAL_EVENT, handleNativeCredential);
  }, []);

  useEffect(() => {
    if (isInApp) return;
    // GIS script yükle
    if (!document.querySelector('script[src*="accounts.google.com/gsi"]')) {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = renderGoogleButton;
      document.head.appendChild(script);
    } else {
      // Script zaten yüklüyse, google hazır olana kadar bekle
      const interval = setInterval(() => {
        if (window.google) {
          clearInterval(interval);
          renderGoogleButton();
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, []);

  return (
    <div>
      {isInApp ? (
        <button
          type="button"
          className="btn btn-outline-secondary w-100 rounded-pill d-flex align-items-center justify-content-center gap-2"
          style={{ minHeight: "44px" }}
          onClick={startNativeLogin}
        >
          <i className="bi bi-google"></i>
          {t("login.googleLogin")}
        </button>
      ) : (
        <div
          ref={btnRef}
          style={{ width: "100%", minHeight: "44px", display: "flex", justifyContent: "center" }}
        />
      )}
      {error && (
        <div className="text-danger small mt-1 text-center">
          <i className="bi bi-exclamation-circle me-1"></i>{error}
        </div>
      )}
      <div className="text-muted text-center mt-2" style={{ fontSize: "0.75rem" }}>
        {t("legal.googleNotice").split(/({{terms}}|{{privacy}})/).map((part, i) => {
          const doc = part === "{{terms}}" ? "terms" : part === "{{privacy}}" ? "privacy" : null;
          if (!doc) return part;
          return (
            <a key={i} href={LEGAL_PATHS[doc]} target="_blank" rel="noopener noreferrer" style={{ color: "#c20e1a" }}>
              {t(`legal.${doc}`)}
            </a>
          );
        })}
      </div>
    </div>
  );
}

export default GoogleLoginButton;
